import { useEffect, useState } from "react";
import { Shield, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { API } from "../../lib/api";
import LoveScreen from "./LoveScreen";

const SESSION_KEY = "kn_access_ok";
const REMEMBER_KEY = "kn_access_remember";
const ROLE_SESSION_KEY = "kn_access_role";
const ROLE_REMEMBER_KEY = "kn_access_role_remember";

function isUnlocked() {
  return sessionStorage.getItem(SESSION_KEY) === "1" || localStorage.getItem(REMEMBER_KEY) === "1";
}

export function getAccessRole() {
  if (!isUnlocked()) return "owner";
  return sessionStorage.getItem(ROLE_SESSION_KEY) || localStorage.getItem(ROLE_REMEMBER_KEY) || "owner";
}

export default function AccessGate({ children }) {
  const [checking, setChecking] = useState(true);
  const [gated, setGated] = useState(false);
  const [unlocked, setUnlocked] = useState(() => (isUnlocked() ? (getAccessRole() === "love" ? "love" : true) : false));
  const [welcome, setWelcome] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/access/status`)
      .then((r) => (r.ok ? r.json() : { gated: true }))
      .then((d) => {
        setGated(!!d.gated);
        setChecking(false);
        if (!d.gated) setUnlocked(true);
      })
      .catch(() => {
        setChecking(false);
        setGated(true);
      });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API}/access/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (res.status === 429) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || "Çok fazla hatalı deneme. Bir süre bekleyin.");
        return;
      }
      if (!res.ok) {
        setError("Şifre hatalı. Tekrar deneyin.");
        return;
      }
      const d = await res.json().catch(() => ({}));
      const role = d.role || "owner";
      sessionStorage.setItem(SESSION_KEY, "1");
      sessionStorage.setItem(ROLE_SESSION_KEY, role);
      if (remember) {
        localStorage.setItem(REMEMBER_KEY, "1");
        localStorage.setItem(ROLE_REMEMBER_KEY, role);
      }
      setPassword("");
      if (role === "love") {
        setUnlocked("love");
      } else if (role === "limited") {
        setWelcome(d.name || "Salih Bey");
      } else {
        setUnlocked(true);
      }
    } catch {
      setError("Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edin.");
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <div className="access-loading">
        <Loader2 size={26} className="spin" />
      </div>
    );
  }

  if (welcome) {
    return (
      <div className="access-gate">
        <div className="access-card" data-testid="access-welcome">
          <div className="access-logo-wrap">
            <div className="access-halo"></div>
            <div className="access-logo">
              <img src="https://static.prod-images.emergentagent.com/jobs/fe9a795a-9b27-4bfd-9166-eea07adda0cf/images/de0f25fef2e7b6a99671adb5dfe2d7eadd23991c138ad11a61234e9051ce02e1.jpeg" alt="Kemal Namlı Çiftliği" />
            </div>
          </div>
          <h1 data-testid="access-welcome-title">Hoşgeldiniz {welcome}</h1>
          <button className="access-submit" data-testid="access-welcome-continue" onClick={() => { setWelcome(""); setUnlocked(true); }}>
            <Shield size={16} />
            Devam Et
          </button>
        </div>
      </div>
    );
  }

  if (unlocked === "love") return <LoveScreen />;

  if (!gated || unlocked) return children;

  return (
    <div className="access-gate">
      <div className="access-card">
        <div className="access-logo-wrap">
          <div className="access-halo"></div>
          <div className="access-logo">
            <img src="https://static.prod-images.emergentagent.com/jobs/fe9a795a-9b27-4bfd-9166-eea07adda0cf/images/de0f25fef2e7b6a99671adb5dfe2d7eadd23991c138ad11a61234e9051ce02e1.jpeg" alt="Kemal Namlı Çiftliği" />
          </div>
        </div>
        <h1>Kemal Namlı Çiftliği</h1>
        <p>Uygulamaya giriş için şifre girin</p>

        <form onSubmit={submit} className="access-form">
          <div className="access-input-wrap">
            <Lock size={16} />
            <input
              type={show ? "text" : "password"}
              autoFocus
              required
              data-testid="access-password-input"
              placeholder="Şifre"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(""); }}
              autoComplete="current-password"
            />
            <button type="button" className="access-eye" data-testid="access-toggle-visibility" onClick={() => setShow(!show)}>
              {show ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>

          <label className="access-remember">
            <input type="checkbox" data-testid="access-remember" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            <span>Bu cihazda beni hatırla</span>
          </label>

          {error && <div className="access-error" data-testid="access-error">{error}</div>}

          <button type="submit" className="access-submit" data-testid="access-submit" disabled={busy || !password}>
            {busy ? <Loader2 size={16} className="spin" /> : <Shield size={16} />}
            {busy ? "Kontrol ediliyor..." : "Giriş"}
          </button>
        </form>

        <small className="access-hint">Şifreniz üçüncü kişilerle paylaşılmamalıdır.</small>
      </div>
    </div>
  );
}
